import { fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { task } from 'ember-concurrency';
// @ts-expect-error: Incorrect type
import stopPropagation from 'ember-event-helpers/helpers/stop-propagation';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import { local } from 'embroider-css-modules';
import {
  CURRENCY_MAPPING,
  type SupportedCurrencies,
} from 'frontend-utils/utils/constants/currency-mapping-constants';
import GeneralCurrencyPickerMenu from 'lumen/components/general/currency-picker/menu';
import type { Currency, FavouriteCurrency } from 'lumen/models';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

import styles from './currency-picker.module.css';

interface GeneralCurrencyPickerSignature {
  Args: {
    defaultCurrencyTooltip: string;
    fallbackCurrencyCode: SupportedCurrencies;
    onCurrencyChange?: (currency: SupportedCurrencies | null) => unknown;
  };
  Blocks: {
    footer?: [];
  };
}

export default class GeneralCurrencyPicker extends Component<GeneralCurrencyPickerSignature> {
  @service declare alerting: Services['alerting'];
  @service declare currency: Services['currency'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked searchValue = '';

  fetchCurrencies = modifier(
    (_element, [currency]: [SupportedCurrencies]): void => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.fetchCurrenciesTask.perform(currency);
    },
  );

  addFavouriteTask = task(
    { drop: true },
    async (currency: Currency): Promise<void> => {
      const favouriteCurrency = this.store.createRecord<FavouriteCurrency>(
        'favourite-currency',
        {
          currency,
        },
      );
      await favouriteCurrency.save().catch((error) => {
        favouriteCurrency.rollbackAttributes();

        this.errorTracking.captureException(error);
        this.toast.error(
          this.intl.t('general.favourite_currency.add_failed', {
            currency: currency.code,
          }),
          { showIcon: true },
        );
      });

      this.usageAnalytics.track('Currency Favorited', {
        eventTrigger: 'currency-picker',
        code: currency.code,
      });
    },
  );

  fetchCurrenciesTask = task(
    { restartable: true },
    async (currency: SupportedCurrencies) => {
      const userId = this.hotelAndMarketService.user.id;

      try {
        const currencies = await this.store.query<Currency>('currency', {
          namespace: APIGATEWAY,
          typeKey: `users/${userId}/currencies`,
          reference_currency: currency,
        });

        return currencies.slice();
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  removeFavouriteTask = task(
    { drop: true },
    async (currency: Currency): Promise<void> => {
      if (!currency.favourite) {
        return;
      }

      await currency.favourite.destroyRecord().catch((error) => {
        this.errorTracking.captureException(error);
        this.toast.error(
          this.intl.t('general.favourite_currency.remove_failed', {
            currency: currency.code,
          }),
          {
            showIcon: true,
          },
        );
      });

      this.usageAnalytics.track('Currency Unfavorited', {
        eventTrigger: 'currency-picker',
        code: currency.code,
      });
    },
  );

  toggleFavouriteTask = task(
    { drop: true },
    async (currency: Currency): Promise<void> => {
      if (currency.favourite) {
        await this.removeFavouriteTask.perform(currency);
      } else {
        await this.addFavouriteTask.perform(currency);
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currencies() {
    return this.fetchCurrenciesTask.last?.value ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get favouriteCurrencies() {
    return this.currencies.filter((currency) => {
      return currency.favourite;
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredCurrencies() {
    return this.currencies.filter(
      (currency) =>
        currency.code.toLowerCase().includes(this.searchValue.toLowerCase()) ||
        currency.name.toLowerCase().includes(this.searchValue.toLowerCase()) ||
        currency.symbol.toLowerCase().includes(this.searchValue.toLowerCase()),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isResetCurrencyDisabled() {
    return (
      this.currency.selectedCurrency === null ||
      this.selectedCurrencyCode === this.args.fallbackCurrencyCode
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedCurrencyCode() {
    return this.currency.selectedCurrency ?? this.args.fallbackCurrencyCode;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get triggerLabel() {
    const currencyCode = this.selectedCurrencyCode;
    const currencySymbol = CURRENCY_MAPPING[currencyCode];

    if (!currencySymbol) {
      this.errorTracking.captureMessage(
        `Currency symbol not found for ${currencyCode}`,
      );

      return `${currencyCode} (${currencyCode})`;
    }

    return `${currencyCode} (${currencySymbol})`;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCurrencyLabel(currency: Currency) {
    return `${currency.name} (${currency.symbol})`;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCurrencySubLabel(currency: Currency) {
    if (currency.code === this.selectedCurrencyCode) {
      return undefined;
    }

    const roundedReferenceValue = this.intl.formatNumber(
      currency.referenceValue,
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 5,
      },
    );

    return `1 ${this.selectedCurrencyCode} = ${roundedReferenceValue} ${currency.code}`;
  }

  @action onClose(): void {
    this.searchValue = '';
  }

  @action resetSelectedCurrency(): void {
    this.currency.resetCurrency();
    this.args.onCurrencyChange?.(null);

    this.usageAnalytics.track('Default Currency Selected', {
      eventTrigger: 'currency-picker',
    });
  }

  @action selectCurrency(currency: Currency): void {
    if (currency.code === this.currency.selectedCurrency) {
      return;
    }

    this.currency.setCurrency(currency.code);
    this.args.onCurrencyChange?.(currency.code);

    this.usageAnalytics.track('Currency Changed', {
      eventTrigger: 'currency-picker',
      code: currency.code,
    });
  }

  <template>
    <div class="relative">
      <div
        data-test-id="currency-picker-container"
        {{this.fetchCurrencies this.selectedCurrencyCode}}
      >
        <GeneralCurrencyPickerMenu
          @onClose={{this.onClose}}
          @triggerLabel={{this.triggerLabel}}
          class={{styles.picker}}
          data-test-id="currency-picker"
        >
          <:header>
            <div class="flex">
              <PrismInput
                @autofocus={{true}}
                @iconAfter={{hash name="search"}}
                @onInput={{pick "target.value" (fn (mut this.searchValue))}}
                @placeholder={{t "general.search_currency"}}
                @removePadding={{true}}
                @value={{this.searchValue}}
                data-test-id="currency-picker-search"
              />
            </div>
          </:header>

          <:default as |Menu|>
            {{#if this.favouriteCurrencies.length}}
              <Menu.group
                @label={{t "general.favourites"}}
                data-test-id="currency-picker-group-favourites"
              >
                {{#each this.favouriteCurrencies as |currency|}}
                  <Menu.item
                    @onClick={{fn this.selectCurrency currency}}
                    @subLabel={{this.getCurrencySubLabel currency}}
                    data-test-id="currency-picker-item"
                  >
                    <:default>
                      <div class="flex">
                        <span
                          class="truncate"
                          title={{currency.name}}
                        >{{currency.name}}</span>
                        &nbsp;
                        <span> ({{currency.symbol}}) </span>
                      </div>
                    </:default>
                    <:actions>
                      <PrismButton
                        @buttonType="ghost"
                        @icon="star-filled"
                        @onClick={{stopPropagation
                          (fn this.toggleFavouriteTask.perform currency)
                        }}
                        class={{local
                          styles
                          "favourite-button"
                          "favourite-button--active"
                        }}
                        data-test-id="currency-picker-favourite-button"
                      />
                    </:actions>
                  </Menu.item>
                {{/each}}
              </Menu.group>
            {{/if}}

            <Menu.group
              @label={{t "general.all"}}
              data-test-id="currency-picker-group-all"
            >
              {{#each this.filteredCurrencies as |currency|}}
                <Menu.item
                  @onClick={{fn this.selectCurrency currency}}
                  @subLabel={{this.getCurrencySubLabel currency}}
                  data-test-id="currency-picker-item"
                >
                  <:default>
                    <div class="flex">
                      <span
                        class="truncate"
                        title={{currency.name}}
                      >{{currency.name}}</span>
                      &nbsp;
                      <span> ({{currency.symbol}}) </span>
                    </div>
                  </:default>
                  <:actions>
                    <PrismButton
                      @buttonType="ghost"
                      @icon={{if
                        currency.isFavourite
                        "star-filled"
                        "star-outline"
                      }}
                      @onClick={{stopPropagation
                        (fn this.toggleFavouriteTask.perform currency)
                      }}
                      class={{local
                        styles
                        "favourite-button"
                        (if currency.isFavourite "favourite-button--active")
                      }}
                      data-test-id="currency-picker-favourite-button"
                    />
                  </:actions>
                </Menu.item>
              {{else}}
                <div
                  class="text-300--regular flex min-h-1000 items-center px-300"
                >
                  {{t "general.no_results_found"}}
                </div>
              {{/each}}
            </Menu.group>

          </:default>

          <:footer>
            <div class="flex items-center justify-end gap-200 p-200">
              <div class="flex-1">
                {{yield to="footer"}}
              </div>

              <div class="flex items-center">
                <PrismButton
                  @buttonType="secondary"
                  @iconBefore="reset-currency"
                  @isDisabled={{this.isResetCurrencyDisabled}}
                  @label={{t "general.reset"}}
                  @onClick={{this.resetSelectedCurrency}}
                  data-test-id="currency-picker-reset-to-default"
                />
                {{#if this.isResetCurrencyDisabled}}
                  <PrismTooltip @text={{@defaultCurrencyTooltip}} />
                {{/if}}
              </div>
            </div>
          </:footer>
        </GeneralCurrencyPickerMenu>
      </div>
    </div>
  </template>
}
